import { Button, Divider, Flex, Menu, Text, TextInput, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconSearch } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useClients } from 'src/clients/hooks/useClients';
import classes from './UnderlineTabs.module.css';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { formatName } from 'src/utils/nameFormatting';
import { matchesClientSearch } from 'src/utils/clientSearch';

type ClientSearchProps = {
  width?: number;
  prefix?: string;
};

type SearchInputProps = {
  value: string;
  onChange: (value: string) => void;
};

export function ClientSearch({ width, prefix }: ClientSearchProps) {
  return prefix ? (
    <Flex gap="xxs" align="center">
      <Text size={'xxl'} fw={600}>
        {prefix}
      </Text>
      <ClientSearchMenu width={width} />
    </Flex>
  ) : (
    <ClientSearchMenu />
  );
}

function ClientSearchMenu({ width }: ClientSearchProps) {
  const { clients } = useClients();
  const { clientId, client } = useCurrentClient();
  const [search, setSearch] = useState('');
  const [menuOpened, setMenuOpened] = useState(false);
  const theme = useMantineTheme();
  const clientName = formatName(client?.firstName, client?.lastName);
  const { pathname } = useLocation();

  const results =
    search.length > 0
      ? clients
          .filter(c => matchesClientSearch([c.firstName, c.lastName, c.emailAddress], search))
          .slice(0, 3)
      : [];

  return (
    <Menu
      position="bottom-start"
      shadow="md"
      opened={menuOpened}
      onClose={() => setMenuOpened(false)}
      classNames={{
        dropdown: classes.clientSearchDropdown,
      }}
    >
      <Menu.Target>
        <Button
          variant="subtle"
          style={{
            fontWeight: 600,
            color: theme.colors.brand[6],
            fontSize: theme.fontSizes.xxl,
          }}
          px={0}
          styles={{
            root: {
              width,
            },
            label: {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          rightSection={
            menuOpened ? (
              <IconChevronUp size={18} color={theme.colors.brand[7]} />
            ) : (
              <IconChevronDown size={18} color={theme.colors.brand[7]} />
            )
          }
          onClick={() => setMenuOpened(o => !o)}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {clientName || 'Select Client'}
          </span>
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Flex direction="column" gap="xxs" w={220}>
          <SearchInput value={search} onChange={setSearch} />
          {results.length > 0 && <Divider color={'gray.0'} />}
          {results.map(clientResult => {
            const href = pathname.replace(clientId, clientResult.id);
            return (
              <Menu.Item
                component={Link}
                key={clientResult.id}
                to={href}
                onClick={() => {
                  setSearch('');
                  setMenuOpened(false);
                }}
              >
                {formatName(clientResult.firstName, clientResult.lastName)}
              </Menu.Item>
            );
          })}
        </Flex>
      </Menu.Dropdown>
    </Menu>
  );
}

function SearchInput({ value, onChange }: SearchInputProps) {
  function focusInput(el: HTMLInputElement | null) {
    if (el) {
      requestAnimationFrame(() => {
        el.focus();
      });
    }
  }

  return (
    <TextInput
      ref={focusInput}
      placeholder="Search clients"
      value={value}
      onChange={e => onChange(e.target.value)}
      classNames={{
        input: classes.clientSearchInput,
      }}
      rightSection={<IconSearch color={'#E5E5E5'} />}
      autoFocus
    />
  );
}
